import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Tag, X, Loader2, CheckCircle } from "lucide-react";
import { useI18n } from "../../i18n";

const API = "/api";

// CORTE 5-A — campo de cupón discreto. El frontend JAMÁS calcula montos:
// muestra exclusivamente lo que devuelve POST /api/coupon/validate.
export default function CouponField({ months, onChange, initialCode = "", dark = false }) {
  const { t } = useI18n();
  const [code, setCode] = useState(initialCode);
  const [status, setStatus] = useState("idle"); // idle | checking | applied | error
  const [quote, setQuote] = useState(null);
  const [errKey, setErrKey] = useState(null);
  const [errVars, setErrVars] = useState(null);
  const timerRef = useRef(null);
  const seqRef = useRef(0);
  const cacheRef = useRef({}); // "CODE|months" -> resultado (no revalidar lo ya validado)

  const notify = (q) => { if (onChange) onChange(q); };

  const applyResult = (data) => {
    if (data?.valid) {
      setQuote(data); setStatus("applied"); setErrKey(null); setErrVars(null); notify(data);
    } else {
      setQuote(null); setStatus("error");
      setErrKey(`checkout.coupon_err_${data?.reason || "invalid"}`); setErrVars(null);
      notify(null);
    }
  };

  const validate = async (value, m) => {
    const seq = ++seqRef.current;
    const clean = (value || "").trim().toUpperCase();
    if (!clean) {
      setStatus("idle"); setQuote(null); setErrKey(null); setErrVars(null); notify(null);
      return;
    }
    if (clean.length < 3) { setStatus("idle"); setQuote(null); setErrKey(null); notify(null); return; }
    const cacheKey = `${clean}|${m}`;
    if (cacheRef.current[cacheKey]) { applyResult(cacheRef.current[cacheKey]); return; }
    setStatus("checking");
    try {
      const r = await axios.post(`${API}/coupon/validate`, { code: clean, months: m });
      if (seq !== seqRef.current) return;
      cacheRef.current[cacheKey] = r.data;
      applyResult(r.data);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setQuote(null); setStatus("error");
      if (e.response?.status === 429) {
        const mins = e.response?.data?.detail?.retry_after_min || 60;
        setErrKey("checkout.coupon_err_rate"); setErrVars({ minutes: mins });
      } else {
        setErrKey("checkout.coupon_err_invalid"); setErrVars(null);
      }
      notify(null);
    }
  };

  // Validación mientras escribe: retardo 800ms tras la ÚLTIMA tecla + mínimo 3 caracteres
  const handleChange = (e) => {
    const v = e.target.value;
    setCode(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => validate(v, months), 800);
  };

  // F1.3 — revalidación automática al cambiar de plan
  useEffect(() => {
    if (code.trim()) validate(code, months);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months]);

  // Prefill (voucher rechazado: el cupón sigue aplicado sin re-escribirlo)
  useEffect(() => {
    if (initialCode && !code) {
      setCode(initialCode);
      validate(initialCode, months);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCode]);

  const remove = () => {
    seqRef.current++;
    setCode(""); setQuote(null); setStatus("idle"); setErrKey(null); notify(null);
  };

  const base = dark
    ? { label: "text-slate-400", input: "bg-white/5 border-white/15 text-white placeholder-slate-500", chip: "bg-emerald-500/15 border-emerald-400/40 text-emerald-300" }
    : { label: "text-slate-600", input: "bg-white border-slate-200 text-slate-900 placeholder-slate-400", chip: "bg-emerald-50 border-emerald-300 text-emerald-700" };

  if (status === "applied" && quote) {
    const d = quote.display;
    return (
      <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${base.chip}`} data-testid="coupon-chip">
        <CheckCircle className="w-4 h-4 flex-shrink-0" />
        <div className="flex-1 text-sm">
          <span className="font-bold font-mono">{quote.code}</span>
          <span className="ml-2">{t("checkout.coupon_applied")}</span>
          <span className="block text-xs" data-testid="coupon-savings">
            {t("checkout.coupon_savings", { amount: `${d.symbol} ${Number(d.discount).toLocaleString()}` })}
            {quote.partner_name ? ` · ${quote.partner_name}` : ""}
          </span>
        </div>
        <button type="button" onClick={remove} className="opacity-70 hover:opacity-100" data-testid="coupon-remove-btn" title={t("checkout.coupon_remove")}>
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <label className={`text-xs font-medium flex items-center gap-1.5 mb-1 ${base.label}`}>
        <Tag className="w-3.5 h-3.5" /> {t("checkout.coupon_label")}
      </label>
      <div className="relative">
        <input
          type="text"
          value={code}
          onChange={handleChange}
          placeholder={t("checkout.coupon_placeholder")}
          className={`w-full rounded-xl border px-3 py-2 text-sm font-mono uppercase outline-none focus:border-emerald-400 ${base.input}`}
          maxLength={32}
          data-testid="coupon-input"
        />
        {status === "checking" && (
          <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-2.5 text-slate-400" data-testid="coupon-checking" />
        )}
      </div>
      {status === "error" && errKey && (
        <p className="text-xs text-red-500 mt-1" data-testid="coupon-error">{t(errKey, errVars || undefined)}</p>
      )}
    </div>
  );
}
